import React, { useEffect, useState } from "react";
import { Smartphone, X, Share } from "lucide-react";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

// Eligible = a repeat rider (2+ visits), on a phone-capable browser, not already installed,
// and hasn't dismissed the banner. Shared with InstallPrompt so we never show two CTAs.
export const bannerEligible = () => {
  if (isStandalone()) return false;
  if (localStorage.getItem("nl_install_dismissed") === "1") return false;
  return Number(localStorage.getItem("nl_visits") || "0") >= 2;
};

export const InstallBanner = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    // iOS never fires beforeinstallprompt — show the banner with manual instructions instead.
    const t = setTimeout(() => { if (bannerEligible()) setShow(true); }, 1200);
    const onInstalled = () => setShow(false);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      clearTimeout(t);
    };
  }, []);

  // On Android/desktop show once we actually have a prompt; on iOS show regardless.
  const visible = show && (deferred || isIOS());
  if (!visible) return null;

  const dismiss = () => { localStorage.setItem("nl_install_dismissed", "1"); setShow(false); };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch { /* ignore */ }
    setDeferred(null);
    setShow(false);
  };

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-[70] bg-[var(--nl-bg-2)]/95 backdrop-blur-md border-t border-[var(--nl-border-gold)] px-5 py-4 flex items-center gap-4 shadow-[0_-8px_30px_rgba(0,0,0,0.4)]"
      data-testid="install-banner"
    >
      <div className="w-10 h-10 shrink-0 flex items-center justify-center rounded-full bg-gold/10 border border-gold/30">
        <Smartphone size={18} className="text-gold" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-body text-sm">Add <span className="text-gold">NamarckLimo</span> to your phone</p>
        {isIOS() && !deferred ? (
          <p className="text-white/50 text-xs font-body flex items-center gap-1 mt-0.5">
            Tap <Share size={12} className="inline text-gold" /> then “Add to Home Screen” for one-tap booking &amp; live tracking.
          </p>
        ) : (
          <p className="text-white/50 text-xs font-body mt-0.5">One tap to book and track your chauffeur live — no app store needed.</p>
        )}
      </div>
      {deferred && (
        <button onClick={install} data-testid="install-banner-btn" className="label bg-gold text-black px-4 py-2.5 whitespace-nowrap hover:bg-[var(--nl-gold-hover)] transition-colors duration-300">
          Install
        </button>
      )}
      <button onClick={dismiss} aria-label="Dismiss" data-testid="install-banner-dismiss" className="text-white/40 hover:text-white transition-colors duration-300 shrink-0">
        <X size={20} />
      </button>
    </div>
  );
};
